<template>
  <div>
    <!-- Base Modal - Used for both Initial and Final states -->
    <BaseModal
      ref="baseModal"
      :modal-id="modalId"
      :modal-text="modalText"
      :button-text="buttonText"
      :button-full-width="currentState === 'final'"
      @button-click="handleBaseModalAction"
      v-if="isBaseModalVisible"
    />

    <!-- Job Description Modal -->
    <JobDescriptionModal
      ref="jobModal"
      v-if="isJobModalVisible"
      :currentJob="currentJob"
      :currentIndex="currentJobIndex"
      :totalJobs="jobs.length"
      :loading="loading"
      :company="company"
      :isInitialScout="true"
      :errorMessage="errorMessage"
      :isJobScouted="isCurrentJobScouted"
      :hasReachedScoutLimit="hasReachedScoutLimit"
      :hasCompanyActiveScout="hasCompanyActiveScout"
      @prev="handlePrevJob"
      @next="handleNextJob"
      @scout="openScoutConfirmation"
      @close="handlePopupClose"
    />

    <!-- Scout Confirmation Modal -->
    <ScoutConfirmationModal
      v-if="showScoutConfirmation"
      @confirm="handleScoutConfirm"
      @cancel="handleScoutCancel"
      ref="scoutConfirmModal"
      :loading="loading"
    />
  </div>
</template>

<script>
import Api from "@/config/api";
import BaseModal from "./BaseModal.vue";
import JobDescriptionModal from "./JobDescriptionModal.vue";
import ScoutConfirmationModal from "./ScoutConfirmationModal.vue";
import { mapGetters } from "vuex";
export default {
  name: "ScoutJob",
  components: {
    BaseModal,
    JobDescriptionModal,
    ScoutConfirmationModal,
  },
  data() {
    return {
      // Modal States: 'jobs' | 'scoutSuccess' | 'final' | 'done'
      currentState: "jobs",

      // Job Data
      jobs: [], // Array of 3 jobs
      currentJobIndex: 0,
      scoutedJobs: [], // Track scouted jobs

      // Modal Visibility
      showScoutConfirmation: false,
      errorMessage: '',
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      profile: 'getProfile',
    }),
    company() {
      return this.profile.company_name;
    },
    isBaseModalVisible() {
      return this.currentState === "scoutSuccess" || this.currentState === "final";
    },

    isJobModalVisible() {
      return this.currentState === "jobs";
    },

    modalId() {
      return "finalModal";
    },

    modalText() {
      if (this.currentState === "scoutSuccess") {
        return "スカウト希望が完了しました！";
      }
      return "あなたに合う求人の閲覧が完了しました！<br>その他の求人や、<br/>企業イベント情報を閲覧できます";
    },

    buttonText() {
      return "OK";
    },

    // Job related computations
    currentJob() {
      return this.jobs[this.currentJobIndex];
    },

    isLastJob() {
      return this.currentJobIndex === this.jobs.length - 1;
    },

    // Scout status computations
    isCurrentJobScouted() {
      if (!this.currentJob) return false;
      return this.isJobScoutedById(this.currentJob._id);
    },

    hasReachedScoutLimit() {
      if (this.scoutedJobs.length >= 10) return true;
      // Check if there's already a scouted job from the same company as current job
      if (!this.currentJob) return false;
      const currentCompanyId = this.currentJob.company_id || this.currentJob.companyId;
      if (!currentCompanyId) return false;
      return this.scoutedJobs.some(job => {
        const jobCompanyId = job.company_id || job.companyId;
        return jobCompanyId && String(jobCompanyId) === String(currentCompanyId);
      });
    },

    hasCompanyActiveScout() {
      // Check if there's already a scouted job from the same company as current job
      if (!this.currentJob || !this.scoutedJobs.length) return false;
      const currentCompanyId = this.currentJob.company_id || this.currentJob.companyId;
      if (!currentCompanyId) return false;
      return this.scoutedJobs.some(job => {
        const jobCompanyId = job.company_id || job.companyId;
        return jobCompanyId && String(jobCompanyId) === String(currentCompanyId);
      });
    },
  },
  methods: {
    async getTopThreeJobs() {
      let loadingTimeout;

      try {
        this.loading = true;
        
        // Set timeout to check loading state after 1 minute
        loadingTimeout = setTimeout(() => {
          if (this.loading) {
            this.loading = false;
            this.errorMessage = "リクエストがタイムアウトしました。恐れ入りますが、再度お試しください。";
            setTimeout(() => {
              this.errorMessage = '';
              if (this.$refs.jobModal) {
                this.$refs.jobModal.hide();
              }
            }, 1500);
          }
        }, 60000);

        await this.$auth.getToken();
        const jobs = await this.axios({
          method: "get",
          url: Api.url(Api.endpoints.initialJobs),
        });

        if (!jobs.data || jobs.data.length === 0) {
          this.errorMessage = "マッチする求人表示にエラーが発生しました。恐れ入りますが、少し時間を置いて再度お試しください。";
          setTimeout(() => {
            this.errorMessage = '';
            if (this.$refs.jobModal) {
              this.$refs.jobModal.hide();
            }
          }, 10000);
          return;
        }

        this.jobs = jobs.data.slice(0, 3);
      } catch (err) {
        console.error("Error fetching jobs:", err);
        this.errorMessage = "マッチする求人表示にエラーが発生しました。恐れ入りますが、少し時間を置いて再度お試しください。";
        setTimeout(() => {
          this.errorMessage = '';
          if (this.$refs.jobModal) {
            this.$refs.jobModal.hide();
          }
        }, 10000);
      } finally {
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
        }
        this.loading = false;
      }
    },
    markScoutPopupViewed() {
      localStorage.setItem('hasViewedScoutPopup', 'true');
    },
    // Base Modal Actions
    handleBaseModalAction() {
      if (this.currentState === "scoutSuccess") {
        this.currentState = "final";
        return;
      }
      if (this.currentState === "final") {
        this.markScoutPopupViewed();
        if (this.$refs.baseModal) {
          this.$refs.baseModal.hide();
        }
        this.currentState = "done";
      }
    },

    closeModal() {
      this.$refs.baseModal.hide();
      if (this.modalId === 'finalModal') {
        this.markScoutPopupViewed();
      }
    },

    handlePopupClose() {
      this.errorMessage = '';
      this.markScoutPopupViewed();
    },



    // Job Modal Actions
    handleNextJob() {
      if (this.isLastJob) {
        this.showFinalState();
      } else {
        this.currentJobIndex++;
      }
    },
    handlePrevJob() {
      if (this.currentJobIndex > 0) {
        this.currentJobIndex--;
      }
    },

    showFinalState() {
      this.currentState = "final";
    },

    // Scout Confirmation Actions
    openScoutConfirmation() {
      this.showScoutConfirmation = true;
      this.$nextTick(() => {
        this.$refs.scoutConfirmModal.show();
      });
    },

    async handleScoutConfirm() {
      try {
        this.loading = true;
        await this.submitScoutRequest(this.currentJob._id);
        this.scoutedJobs.push(this.currentJob);
        this.showScoutConfirmation = false;
        if (this.isLastJob) {
          this.markScoutPopupViewed();
        }
        this.currentState = "scoutSuccess";
      } catch (error) {
        console.error("Scout request failed:", error);
      } finally {
        this.loading = false;
      }
    },

    handleScoutCancel() {
      this.showScoutConfirmation = false;
      this.$nextTick(() => {
        this.$refs.jobModal.show();
      });
    },

    // Scout status methods
    isJobScoutedById(jobId) {
      return this.scoutedJobs.some(job => job._id === jobId);
    },

    // API Methods
    async submitScoutRequest(jobId) {
      try {
        await this.$auth.getToken();
        await this.axios({
          method: "post",
          url: Api.url(Api.endpoints.addToScout),
          data: {
            jobId,
          },
        });
      } catch (err) {
        console.log("Submitting scout request for job:", jobId);
      }
    },
  },
  mounted() {
    // Show modal immediately with loader, then load jobs
    this.$nextTick(() => {
      this.$refs.jobModal.show();
      this.getTopThreeJobs();
    });
  },
  watch: {
    isBaseModalVisible(visible) {
      if (visible) {
        this.$nextTick(() => {
          if (this.$refs.baseModal) {
            this.$refs.baseModal.show();
          }
        });
      }
    },
    isJobModalVisible(newValue) {
      if (newValue) {
        this.getTopThreeJobs();
      }
    },
  },
};
</script>
